import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import SectorInfoCard from './SectorInfoCard';
import SectorDataStatsCard from './SectorDataStatsCard';
import TopStocksTable from './TopStocksTable';

function SectorDetailsDialog({
  open,
  onClose,
  loading,
  sectorCode,
  sectorDetails,
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          overflow: 'hidden',
        }
      }}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : sectorDetails ? (
        <>
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 2,
              position: 'relative',
              overflow: 'visible',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -20,
                right: -20,
                width: 120,
                height: 120,
                background: 'rgba(255, 255, 255, 0.06)',
                borderRadius: '50%',
                zIndex: 0,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '1.25rem',
                    wordBreak: 'break-word',
                    overflow: 'visible',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {sectorDetails.sector_info?.sector_name || sectorCode}
                </Typography>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'white',
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <DialogContent sx={{ p: 3, pb: 3, overflowX: 'hidden' }}>
            {/* Info Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' }, width: '100%', boxSizing: 'border-box' }}>
              <SectorInfoCard
                sectorCode={sectorCode}
                sectorInfo={sectorDetails.sector_info}
              />
              <SectorDataStatsCard
                dataStatistics={sectorDetails.data_statistics}
              />
            </Box>

            {/* Top Stocks Table */}
            <TopStocksTable
              stocks={sectorDetails.stocks}
              totalStocks={sectorDetails.total_stocks}
            />
          </DialogContent>
        </>
      ) : (
        <DialogContent>
          <Alert severity="error">{t('sectorsManagement.details.error')}</Alert>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default SectorDetailsDialog;

